import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils';

const badgeVariants = cva(
	'inline-flex items-center justify-center rounded-full border border-border/60 bg-foreground/90 px-2 py-0.5 text-[11px] font-semibold leading-none text-background shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
	{
		variants: {
			variant: {
				default: 'border-border/60 bg-foreground/90 text-background',
				primary: 'border-primary/50 bg-primary text-primary-foreground',
				secondary: 'border-secondary/60 bg-secondary text-secondary-foreground',
				success: 'border-success/60 bg-success text-success-foreground',
				warning: 'border-warning/60 bg-warning text-warning-foreground',
				accent: 'border-accent/60 bg-accent text-accent-foreground',
				inverted: 'border-inverted/60 bg-inverted text-inverted-foreground',
				destructive: 'border-destructive/70 bg-destructive text-destructive-foreground',
				outline: 'border-border/60 bg-transparent text-foreground',
				ghost: 'border-transparent bg-transparent text-foreground hover:bg-foreground/10',
			},
			size: {
				default: 'px-2 py-0.5 text-[11px]',
				sm: 'px-1.5 py-0.25 text-[10px]',
				lg: 'px-3 py-0.75 text-sm',
				xl: 'px-4 py-1 text-base',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(({ className, variant, size, ...props }, ref) => {
	return <div ref={ref} className={cn(badgeVariants({ variant, size, className }))} {...props} />;
});

Badge.displayName = 'Badge';

export { Badge, badgeVariants };
